<template>
  <div v-if="isSubmitted" v-html="successMessage"></div>
  <div v-else class="footer-form-container d-flex flex-v-start flex-h-between flex-wrap">
    <form @submit.prevent="submit" novalidate>
      <div class="d-flex flex-wrap flex-v-start flex-h-between">
        <div class="input-holder">
          <label for="FirstName">First name*</label>
          <input type="text" id="FirstName" name="FirstName" v-model="request.FirstName" />
          <small class="p-error" v-if="form.FirstName.$error">{{ form.FirstName.$errors[0].$message }}</small>
        </div>
        <div class="input-holder">
          <label for="LastName">Last name*</label>
          <input type="text" id="LastName" name="LastName" v-model="request.LastName" />
          <small class="p-error" v-if="form.LastName.$error">{{ form.LastName.$errors[0].$message }}</small>
        </div>
      </div>
      <div class="d-flex flex-wrap flex-v-start flex-h-between">
        <div class="input-holder">
          <label for="Email">Email*</label>
          <input type="email" id="Email" name="Email" v-model="request.Email" />
          <small class="p-error" v-if="form.Email.$error">{{ form.Email.$errors[0].$message }}</small>
        </div>
        <div class="input-holder">
          <label for="PhoneNumber">Phone*</label>
          <div class="d-block">
            <IntlTelInput ref="phoneNumberInput" v-model="phoneNumber" :options="phoneOptions"
              @change-validity="(valid: boolean) => (phoneNumberIsValid = valid)" />
          </div>
          <small class="p-error" v-if="form.PhoneNumber.$error">{{ form.PhoneNumber.$errors[0].$message }}</small>
        </div>
      </div>
      <div class="form-button" style="margin-bottom: 30px">
        <button type="submit" class="btn" :loading="isSubmitting">Sign Up Now</button>
      </div>
      <p style="margin-bottom: 30px">By clicking 'Sign up now' you agree to the <a class="form-link" :href="privacyPolicyUrl" target="_blank">Privacy Policy</a> and <a
        :href="termsAndConditionsUrl" target="_blank">Terms and Conditions</a>.</p>
      <div class="d-flex flex-wrap flex-v-start">
        <div class="input-holder wide">
          <div class="input-holder has-checkbox wide">
            <input type="checkbox" id="AgreeToPromotions" name="AgreeToPromotions" v-model="request.AgreeToPromotions" />
            <label for="AgreeToPromotions">Please tick if you wish to receive promotions, news and the latest information about upcoming events from
              All Blacks Travel & Hospitality.</label>
          </div>
        </div>
      </div>
      <div class="d-flex flex-wrap flex-v-start">
        <div class="input-holder wide">
          <div class="input-holder has-checkbox wide">
            <input type="checkbox" id="AgreeToShareDetails" name="AgreeToShareDetails" v-model="request.AgreeToShareDetails" />
            <label for="AgreeToShareDetails">Please share the details I provided with New Zealand Rugby Commercial (NZRC) so I can receive the latest
              rugby news, content, promotions and partner offers by email from NZRC in accordance with the <a class="form-link"
                :href="nzrcPrivacyPolicyUrl" target="_blank">NZRC Privacy Policy</a>.</label>
          </div>
        </div>
      </div>
      <div class="d-flex flex-wrap flex-v-start">
        <div class="input-holder wide">
          <div class="input-holder has-checkbox wide">
            <input type="checkbox" id="AgreeToSTHPromotions" name="AgreeToSTHPromotions" v-model="request.AgreeToSTHPromotions" />
            <label for="AgreeToSTHPromotions">Please tick if you wish to receive promotions, news and the latest information about
              similar products from <a class="form-link" :href="sthUrl" target="_blank">Sports, Travel & Hospitality Group (STH)</a>.</label>
          </div>
        </div>
      </div>
    </form>
  </div>
</template>
<script lang="ts" setup>
import type { ApiResult } from '@/types/ApiResult';
import useVuelidate from '@vuelidate/core';
import { email, helpers, required } from '@vuelidate/validators';
import 'intl-tel-input/styles';
import IntlTelInput from 'intl-tel-input/vueWithUtils';
import { computed, ref, watch } from 'vue';
import { useReCaptcha } from 'vue-recaptcha-v3';
import wretch from 'wretch';
import { NewsletterSignUpFormRequestModel } from './types';

const recaptcha = useReCaptcha();

const props = defineProps({
  umbracoNodeId: Number,
  sthUrl: String,
  nzrcPrivacyPolicyUrl: String,
  privacyPolicyUrl: String,
  termsAndConditionsUrl: String,
  successMessage: String,
});

const isSubmitting = ref<boolean>(false);
const isSubmitted = ref<boolean>(false);

const phoneNumberInput = ref<typeof IntlTelInput>(null!);
const phoneNumber = ref<string>();
watch(phoneNumber, () => (request.value.PhoneNumber = phoneNumberInput.value.instance.getNumber()));

const phoneOptions = ref({ initialCountry: 'nz', preferredCountries: ['nz', 'au'], nationalMode: true, formatOnDisplay: true });
const phoneNumberIsValid = ref<boolean>(false);
const validatePhoneNumber = (value: string | null) => !helpers.req(value) || phoneNumberIsValid.value;

const rules = computed<Partial<Record<keyof NewsletterSignUpFormRequestModel, unknown>>>(() => ({
  FirstName: { required: helpers.withMessage('Required field', required) },
  LastName: { required: helpers.withMessage('Required field', required) },
  Email: { required: helpers.withMessage('Required field', required), email: helpers.withMessage('Invalid email address', email) },
  PhoneNumber: { required: helpers.withMessage('Required field', required), isValid: helpers.withMessage('Invalid phone number', validatePhoneNumber) },
}));

const request = ref<NewsletterSignUpFormRequestModel>(
  new NewsletterSignUpFormRequestModel({
    UmbracoNodeId: props.umbracoNodeId,
  })
);
const form = useVuelidate<NewsletterSignUpFormRequestModel>(rules, request);

function scrollToSuccess() {
  const yay = document.getElementById('successMessageContainer');
  yay?.scrollIntoView({ behavior: 'smooth' });
}

async function submit() {
  if (!(await form.value.$validate())) {
    return;
  }

  try {
    isSubmitting.value = true;

    if (recaptcha) {
      await recaptcha.recaptchaLoaded();
      request.value.RecaptchaToken = await recaptcha.executeRecaptcha('enquiry');
    }

    const resp = await wretch('/api/contact/newsletter-signup').post(request.value).json<ApiResult<object>>();
    if (resp.successful) {
      request.value = new NewsletterSignUpFormRequestModel({
        UmbracoNodeId: props.umbracoNodeId,
      });
      phoneNumber.value = undefined;
      form.value.$reset();
      isSubmitted.value = true;
      window.setTimeout(function () {
        scrollToSuccess();
      }, 400);
    }
  } finally {
    isSubmitting.value = false;
  }
}
</script>
